/*
  «Ход загрузки» - этапы списком с галочками (вариант А макета, решение
  владельца 19.10.2026).

  Четыре этапа: отправка файла, разбор файла, проверка строк, запись в
  портфель. Пройденный этап - зелёная галочка, время и короткий итог; у
  текущего крутится значок и бежит полоса с числом «строка 4 120 из 9 516»;
  сверху - сколько всё идёт. Так работа отличается от зависания, а конец - от
  середины.

  Разметка только: всё состояние приходит из `useUploadFlow`.
*/

import { useEffect, useState } from 'react'

import { date, plural } from '../../api/format.js'
import DownloadLink from './DownloadLink.jsx'

const rowsWord = (count) => plural(count, 'строка', 'строки', 'строк')
const NUMBER = new Intl.NumberFormat('ru-RU')
const seconds = (value) => (value === undefined || value === null ? '' : `${Math.max(1, Math.round(value))} с`)

// Меньше мегабайта - в килобайтах: «0 МБ отправлены» у таблицы на три строки
// читалось как «ничего не ушло» (осмотр 20.10.2026).
function megabytes(size) {
  const digits = { maximumFractionDigits: 1 }
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024)).toLocaleString('ru-RU')} КБ`
  return `${(size / (1024 * 1024)).toLocaleString('ru-RU', digits)} МБ`
}

function Mark({ state, number }) {
  if (state === 'done') {
    return (
      <span className="upload-stage__mark" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 16 16">
          <path d="M3 8.5l3.5 3.5L13 5" fill="none" stroke="currentColor" strokeWidth="2.4" />
        </svg>
      </span>
    )
  }
  return (
    <span className="upload-stage__mark" aria-hidden="true">
      {state === 'now' ? '' : state === 'fail' ? '!' : number}
    </span>
  )
}

function Track({ share }) {
  // Ширина полосы - вычисленное значение: классом её не задать (правило 6 скилла).
  const width = share === null ? 100 : Math.round(Math.min(1, Math.max(0, share)) * 100)
  return (
    <div className={`upload-track upload-track--live${share === null ? ' upload-track--unknown' : ''}`}>
      <span style={{ width: `${width}%` }} />
    </div>
  )
}

function Stage({ number, title, state, time, fact, share }) {
  return (
    <li className="upload-stage" data-state={state}>
      <Mark state={state} number={number} />
      <span className="upload-stage__title">{title}</span>
      <span className="upload-stage__time">{time}</span>
      {fact ? <span className="upload-stage__fact">{fact}</span> : null}
      {state === 'now' && share !== undefined ? <Track share={share} /> : null}
    </li>
  )
}

function sending(flow) {
  if (flow.phase === 'idle') return { state: 'wait' }
  if (flow.phase === 'sending') {
    return { state: 'now', fact: `отправлено ${Math.round(flow.sent * 100)}%`, share: flow.sent }
  }
  if (!flow.sentAt) return { state: 'fail', fact: 'файл не отправлен' }
  return {
    state: 'done',
    time: seconds((flow.sentAt - flow.startedAt) / 1000),
    fact: `${megabytes(flow.file?.size_bytes ?? 0)} отправлены, больше файл отправлять не нужно`,
  }
}

function reading(flow) {
  const file = flow.file
  if (!file || flow.phase === 'sending' || flow.phase === 'idle') return { state: 'wait' }
  if (flow.phase === 'reading') {
    const sheet = file.sheets > 1 ? `лист ${file.sheet + 1} из ${file.sheets} · ` : ''
    const of = file.rows_total ? ` из ${NUMBER.format(file.rows_total)}` : ''
    return {
      state: 'now',
      fact: `${sheet}строка ${NUMBER.format(file.rows_done)}${of}`,
      share: file.rows_total ? file.rows_done / file.rows_total : null,
    }
  }
  if (flow.phase === 'failed') return { state: 'fail', fact: 'файл не прочитан - причина ниже' }
  return {
    state: 'done',
    time: seconds((flow.readAt - flow.sentAt) / 1000),
    fact: file.rows_done
      ? `прочитано ${rowsWord(file.rows_done)} - дальше все действия по готовым данным`
      : 'прочитан - дальше все действия по готовым данным',
  }
}

function checking(flow, hint) {
  const check = flow.checked
  if (!check) return { state: 'wait', fact: hint }
  if (check.state === 'now') return { state: 'now', share: null }
  return { state: check.state, time: seconds(check.seconds), fact: check.fact }
}

function writing(flow) {
  const write = flow.written
  if (!write) return { state: 'wait', fact: 'после того, как вы нажмёте «Загрузить»' }
  if (write.state === 'now') {
    if (write.stage === 'writing' && write.total) {
      return {
        state: 'now',
        fact: `записано ${NUMBER.format(write.done)} из ${NUMBER.format(write.total)} строк`,
        share: write.done / write.total,
      }
    }
    const words = { checking: 'проверяем строки и счета', finishing: 'обновляем долг и разбор' }
    return { state: 'now', fact: words[write.stage] ?? 'ждём очереди записи', share: null }
  }
  if (write.state === 'fail') return { state: 'fail', time: seconds(write.seconds), fact: 'не записано - причина ниже' }
  return {
    state: 'done',
    time: seconds(write.seconds),
    fact: write.total ? `записано ${rowsWord(write.total)}` : 'записано',
  }
}

function useClock(active) {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!active) return undefined
    const timer = setInterval(() => tick((value) => value + 1), 1000)
    return () => clearInterval(timer)
  }, [active])
}

function Clock({ flow, active }) {
  if (!flow.startedAt) return null
  const finished = flow.written?.state === 'done'
  const end = finished ? flow.written.at + flow.written.seconds * 1000 : Date.now()
  const text = seconds((end - flow.startedAt) / 1000)
  return <span className="upload-stages__clock">{finished ? `готово за ${text}` : active ? `идёт ${text}` : ''}</span>
}

function Kept({ file }) {
  if (!file || file.state !== 'read') return null
  // `div`, а не `p` (ревью 30.10.2026): отказ скачивания - блок `Notice`, а блок внутри
  // абзаца - неверная разметка, и React жаловался на неё в консоли.
  return (
    <div className="upload-kept muted">
      Файл хранится до {date(file.expires_at)} ·{' '}
      <DownloadLink fileId={file.id} filename={file.filename} />
    </div>
  )
}

/*
  `short` - только отправка и разбор (20.10.2026): у второго файла выгрузки 1С,
  телефонов жильцов, своей проверки и записи нет - он едет вместе с первым.
*/
export default function UploadStages({
  flow,
  checkHint = 'после «Посмотреть, что получится»',
  title = 'Ход загрузки',
  short = false,
}) {
  const active =
    flow.phase === 'sending' ||
    flow.phase === 'reading' ||
    flow.checked?.state === 'now' ||
    flow.written?.state === 'now'
  useClock(active)
  if (flow.phase === 'idle') return null
  const stages = [
    ['Отправка файла', sending(flow)],
    ['Разбор файла', reading(flow)],
    ...(short
      ? []
      : [
          ['Проверка строк', checking(flow, checkHint)],
          ['Запись в портфель', writing(flow)],
        ]),
  ]
  return (
    <>
      <section className="upload-stages" aria-live="polite">
        <div className="upload-stages__head">
          <span className="upload-stages__title">{title}</span>
          <Clock flow={flow} active={active} />
        </div>
        <ol>
          {stages.map(([title, stage], index) => (
            <Stage key={title} number={index + 1} title={title} {...stage} />
          ))}
        </ol>
      </section>
      <Kept file={flow.file} />
    </>
  )
}
